<template>
  <v-navigation-drawer
    v-model="globalStore.drawer"
    border="none"
    class="navigation-drawer"
    :class="{
      'rounded-t-lg': mdAndDown
    }"
    :location="lgAndUp ? 'right' : 'bottom'"
    :permanent="lgAndUp"
    touchless
    :width="mdAndUp ? '484' : ''"
  >
    <template v-if="!isLoadingOrganizationData" #prepend>
      <div
        v-if="mdAndDown && brandingsQuery.data.value?.logoUrl"
        class="bg-white mt-4 w-100 d-flex align-center justify-center"
      >
        <SwipeSurface
          @close-bottom-sheet="() => (globalStore.drawer = false)"
        />
      </div>
      <VToolbar class="toolbar rounded-t-lg" color="white">
        <div
          v-if="mdAndDown && !brandingsQuery.data.value?.logoUrl"
          class="w-100 h-100 d-flex align-center justify-center"
        >
          <SwipeSurface
            class="d-flex align-center justify-center ml-8"
            @close-bottom-sheet="() => (globalStore.drawer = false)"
          />
        </div>
        <template #prepend>
          <v-icon
            v-if="mdAndDown"
            color="black mx-6"
            @click="() => (globalStore.drawer = false)"
          >
            far fa-chevron-down
          </v-icon>
          <div class="d-flex align-center justify-center ga-2 pl-3">
            <img
              v-if="brandingsQuery.data.value?.logoUrl"
              alt=""
              class="custom-brand-image"
              :src="brandingsQuery.data.value?.logoUrl"
            />
            <RouterLink v-else-if="mdAndUp" :to="{ path: '/' }">
              <DeepAppLogo logo="deepsign" />
            </RouterLink>
          </div>
        </template>

        <template #append>
          <v-btn
            v-tooltip:bottom="t('deep_ui_kit.support.support')"
            icon="far fa-circle-question"
            size="small"
            @click="supportPanel = !supportPanel"
          ></v-btn>
          <UserAccountMenu />
        </template>
      </VToolbar>
    </template>

    <!-- DRAWER CONTENT -->
    <Loader v-show="isLoadingOrganizationData" />
    <div
      v-show="!isLoadingOrganizationData"
      class="px-3 px-lg-4 pb-2 pt-4 h-lg-auto"
    >
      <template v-if="isCreate && currentDocument && createStore.document">
        <CreateCard />
        <PositionAndMessageCard />
        <!-- ACTIONS -->
        <div class="w-100 pb-4 pb-lg-0">
          <DeepButton
            v-if="lgAndUp"
            ref="sendInvitationsButton"
            :action="proceed"
            :class="{
              'pointer-events-none':
                createStore.fetchingAccountingSettings ||
                createStore.hasNoPaymentMethodAndFreeSes
            }"
            data-test-id="continue"
            :disabled="
              !createStore.hasSignees ||
              createStore.fetchingAccountingSettings ||
              createStore.hasNoPaymentMethodAndFreeSes ||
              createStore.isLoading
            "
            :loading="
              startDocumentPending ||
              clearingEmptyInvitations ||
              clearingEmptyObservers
            "
            :text="buttonText"
          />
        </div>
        <!-- /ACTIONS -->
        <ConfirmationDialog ref="confirmationDialog" />
      </template>
      <div v-else class="mt-3">
        <SealingCard
          v-if="route.name === 'document-seal'"
          :can-sign="canSign"
          :document="currentDocument"
          :signatures="currentDocument.signees"
        />
        <SigningCard
          v-else-if="shouldSign && !shouldApprove"
          :can-add-autograph="canAddAutograph"
          :can-sign="canSign"
          :document="currentDocument"
          :email="userEmail"
          :is-signed-by-user="isSignedByUser"
          :sign="sign"
          :sign-button-enabled="canSign"
          :signatures="currentDocument.signees"
          @update-authority-service="setAuthorityService"
          @update-uploaded-files="setUploadedFiles"
        />
        <ApproverCard v-else-if="shouldApprove" :comment="message" />
        <ObserverCard v-else>
          <!-- ON HOLD ALERT -->
          <v-alert
            v-if="
              currentDocument.documentStatus === DocumentStatus.IN_PROGRESS &&
              isUserPartOfSignees &&
              signStatus !== SignStatus.OBSOLETE
            "
            class="deep-caption my-4"
            type="info"
            variant="outlined"
          >
            {{
              hasOnlySignees
                ? i18n.t(
                    isEnvelope
                      ? 'texts.on_hold_hint_envelope'
                      : 'texts.on_hold_hint'
                  )
                : i18n.t('texts.on_hold_generic_hint')
            }}
          </v-alert>
          <!-- /ON HOLD ALERT -->
        </ObserverCard>
        <DocumentInfo
          :document="currentDocument"
          :signees="currentDocument.signees"
          :sing-key="signKey"
          @update-signature-message="updateSignatureComment"
        />
        <ConfirmationDialog ref="signConfirmationDialog" />
        <TimeOutDialog ref="timeOutDialog" />
        <SignatureCancelledDialog />
        <SignErrorDialog />
        <SignatureEdition ref="signatureEdition" />
      </div>
      <div
        v-if="brandingsQuery.isFetched && brandingsQuery.data.value?.logoUrl"
        class="w-100 mx-auto pa-4 d-flex align-center justify-center text-body-small text-grey-darken-4 font-weight-semibold"
      >
        <p class="mr-n4">
          {{ i18n.t('text.powered_by') }}
        </p>
        <DeepAppLogo class="deep-app-logo" logo="deepsign" />
      </div>
    </div>

    <template v-if="mdAndDown" #append>
      <!-- /DRAWER CONTENT -->
      <div v-if="isCreate" class="bottom-sheet-footer">
        <div class="d-flex align-center justify-center w-100">
          <DeepButton
            :action="proceed"
            data-test-id="continue"
            :disabled="
              !createStore.hasSignees ||
              createStore.hasNoPaymentMethodAndFreeSes
            "
            :loading="startDocumentPending"
            :text="
              invitationsCount && invitationsCount > 0
                ? i18n.t('buttons.send_invitations')
                : i18n.t('buttons.continue')
            "
          />
        </div>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import Loader from '@/components/Loader.vue';
import UserAccountMenu from '@/components/dashboard/header/UserAccountMenu.vue';
import TimeOutDialog from '@/components/dialogs/TimeOutDialog.vue';
import SwipeSurface from '@/components/SwipeSurface.vue';
import SigningCard from '@/components/cards/SigningCard.vue';
import SignErrorDialog from '@/components/dialogs/SignErrorDialog.vue';
import SignatureEdition from '@/components/dialogs/SignatureEdition.vue';
import SignatureCancelledDialog from '@/components/dialogs/SignatureCancelledDialog.vue';
import SealingCard from '@/components/cards/SealingCard.vue';
import PositionAndMessageCard from '@/components/cards/PositionAndMessageCard.vue';
import ObserverCard from '@/components/cards/ObserverCard.vue';
import DocumentInfo from '@/components/signatureArea/DocumentInfo.vue';
import DeepButton from '@/components/buttons/DeepButton.vue';
import CreateCard from '@/components/cards/CreateCard.vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import ApproverCard from '@/components/cards/approver/ApproverCard.vue';
import AuthService from '@/services/authService.ts';
import { DeepAppLogo } from '@deepcloud/deep-ui-lib';
import SignatureService from '@/services/signatureService';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import { DocumentStatus } from '@/types/enums/DocumentStatus';
import { SignStatus } from '@/types/enums/SignStatus';
import { SignatureMode } from '@/types/enums/SignatureMode.ts';
import { base64ToFile } from '@/composables/useUtils';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useEmitter } from '@/composables/useEmitter.ts';
import { useI18n } from 'vue-i18n';
import { useMessageStore } from '@/stores/message.ts';
import { usePreferencesStore } from '@/stores/deepadmin/preferences';
import { useRoute, useRouter } from 'vue-router';
import { useSignStore } from '@/stores/sign.ts';
import { useGlobalStore } from '@/stores/global.ts';
import { useSignContext } from '@/composables/useSignContext';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useDocumentWizard } from '@/composables/create/useDocumentWizard';
import { useDisplay } from 'vuetify/framework';
import { useEmailService } from '@/components/inputs/Emailsuggestion/useEmailService';
import { useTerms } from '@/composables/useTerms';
import { useCreateStore } from '@/stores/create.ts';
import type { Document } from '@/types/Document.ts';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import type { Signee } from '@/types/Signee.ts';
import { useOrganizationPageData } from '@/composables/useOrganizationData.ts';
import { useTheme } from 'vuetify';
import { createPlaceholderPayload } from '@/composables/placement/usePlacementUtility.ts';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import type { Placeholder } from '@/composables/placement/placementTypes.ts';
import { EMAIL_REGEX } from '@deepcloud/field-validators';
import { useGroupService } from '@/composables/useGroupService';
import { useApi } from '@/composables/useApi.ts';
import { useSigningWorkflow } from '@/composables/create/useSigningWorkflow';
import { isPlaceholderForSignee } from '@/utils/signeeSignings.ts';

const supportPanel = defineModel('support-panel', {
  type: Boolean,
  default: false
});

const { deepSignAPI } = useApi();
const { t } = useI18n();
const createStore = useCreateStore();
const { isDocumentAuthRoute, currentDocument } = useDocumentContext();
const { removeDocument: removeDocumentWizard } = useDocumentWizard();
const { getTranslatedTerms } = useTerms();
const signingWorkflow = useSigningWorkflow();
const agreement = ref(false);
const authorityService = ref(null);
const autographData = ref(null);
const clearingEmptyInvitations = ref(false);
const clearingEmptyObservers = ref(false);
const confirmationDialog = ref();
const { mdAndDown, mdAndUp, lgAndUp } = useDisplay();
const globalStore = useGlobalStore();
const emitter = useEmitter();
const i18n = useI18n();
const message = ref('');
const messageStore = useMessageStore();
const pollInterval = ref<number | null>(null);
const preferencesStore = usePreferencesStore();
const router = useRouter();
const route = useRoute();
const theme = useTheme();
const signConfirmationDialog = ref();
const signStore = useSignStore();
const startDocumentPending = ref(false);
const useDefaultAutograph = ref(false);
const uploadedFiles = ref([]);
const { isGuest, userEmail, isSignatureTypeSeal, signKey } = useSignContext();
const { saveRecentlyUsedEmails } = useEmailService(
  (currentDocument.value as Document)?.companyId
);
const { isLoadingOrganizationData, brandingsQuery } = useOrganizationPageData();
const { fetchUnits } = useGroupService(currentDocument.value?.companyId);
const { currentPlaceholders, placeholderTemplateRefs } = injectStrict(
  PlaceHolderComposableKey
);

defineEmits(['update-sign-status']);

emitter.$on('change-autograph', updateChangeAutograph);
emitter.$on('stop-sign-status-polling', stopPolling);
emitter.$on('update-agreement', updateAgreementStatus);
emitter.$on('update-attachments-option', createStore.updateAttachmentsAllow);
emitter.$on('update-authority-service', createStore.updateAuthorityService);
emitter.$on('update-autograph-data', updateSignatureData);
emitter.$on('update-signature-level', createStore.updateSignatureLevel);
emitter.$on('update-signature-message', createStore.updateSignatureMessage);

globalStore.drawer = lgAndUp.value;

onMounted(async () => {
  useDefaultAutograph.value =
    signStore.document?.signContext.defaultAutographUrl !== null;
  if (isDocumentCreateAuthRoute.value) {
    await fetchUnits();
  }
});

onUnmounted(() => {
  emitter.$off('change-autograph', updateChangeAutograph);
  emitter.$off('stop-sign-status-polling', stopPolling);
  emitter.$off('update-agreement', updateAgreementStatus);
  emitter.$off('update-attachments-option', createStore.updateAttachmentsAllow);
  emitter.$off('update-authority-service', createStore.updateAuthorityService);
  emitter.$off('update-autograph-data', updateSignatureData);
  emitter.$off('update-signature-level', createStore.updateSignatureLevel);
  emitter.$off('update-signature-message', createStore.updateSignatureMessage);
  createStore.hasNoPaymentMethodAndFreeSes = false;
  signStore.mobileIdCheckPending = false;
});

const isDocumentCreateAuthRoute = computed(
  () => route.name === 'document-create'
);

const isBasic = computed(() => {
  return (
    currentDocument.value &&
    currentDocument.value.signatureMode === SignatureMode.TIMESTAMP
  );
});

const isCreate = computed(() => {
  return (
    currentDocument.value &&
    currentDocument.value.documentStatus === DocumentStatus.DRAFT
  );
});

const isSign8Verified = computed(
  () =>
    signStore.document &&
    signStore.document.signContext.availableAuthorityServices.sign8 &&
    signStore.document.signContext.availableAuthorityServices.sign8
      .hasRequiredLevel
);

const isMobileIdVerified = computed(
  () =>
    signStore.document &&
    signStore.document.signContext.availableAuthorityServices.ras &&
    signStore.document.signContext.availableAuthorityServices.ras
      .hasRequiredLevel
);

const invitationsCount = computed(() => {
  if (currentDocument.value && currentDocument.value.signees) {
    const invites = currentDocument.value.signees?.filter(
      (signee) =>
        !SignatureService.compareString(signee.email, AuthService.user?.email)
    );
    return invites.length;
  } else {
    return 0;
  }
});

const canAddAutograph = computed(() => {
  const mySignature = SignatureService.getSigneeByEmail(
    AuthService.user?.email,
    currentDocument.value.signees
  );
  return (
    canSign.value &&
    (mySignature?.autographPosition !== null ||
      signStore.document?.documentPolicy.canModifyAutographPosition)
  );
});

const canSign = computed(() => {
  if (isGuest.value) {
    return signStore.document?.signContext.signStatus === SignStatus.PENDING;
  }
  if (AuthService.isAuthenticated) {
    return SignatureService.canSign(
      AuthService.user?.email,
      currentDocument.value
    );
  }
  return false;
});

const destination = computed(() => {
  if (SignatureService.onlyMe(AuthService.user?.email, currentDocument.value)) {
    return `/document/${currentDocument.value.documentId}/sign`;
  }
  return `/document/${currentDocument.value.documentId}/invitations-sent`;
});

const confirmationTitle = computed(() => {
  if (
    createStore.hasEmptyObservers &&
    createStore.hasEmptySignees &&
    createStore.hasEmptyApprovers
  ) {
    // Empty approvers signees and observers
    return i18n.t('titles.undefined_approvers_signees_and_observers');
  } else if (createStore.hasEmptySignees && createStore.hasEmptyApprovers) {
    // Empty signees and approvers
    return i18n.t('titles.undefined_approvers_and_signees');
  } else if (createStore.hasEmptyApprovers && createStore.hasEmptyObservers) {
    // Empty approvers and observers
    return i18n.t('titles.undefined_approvers_and_observers');
  } else if (createStore.hasEmptyObservers && createStore.hasEmptySignees) {
    // Empty signees and observers
    return i18n.t('titles.undefined_signees_and_observers');
  } else if (createStore.hasEmptySignees) {
    // Only empty signees
    return i18n.t('titles.undefined_signees');
  } else if (createStore.hasEmptyApprovers) {
    // Only empty approvers
    return i18n.t('titles.undefined_approvers');
  } else if (createStore.hasEmptyObservers) {
    // Only empty observers
    return i18n.t('titles.undefined_observers');
  } else {
    return i18n.t('titles.no_observers_subscription_title');
  }
});

const confirmationText = computed(() => {
  if (
    createStore.hasEmptyObservers &&
    createStore.hasEmptySignees &&
    createStore.hasEmptyApprovers
  ) {
    // Empty approvers signees and observers
    return i18n.t(
      'titles.undefined_approvers_and_signees_and_observers_confirmation_request'
    );
  } else if (createStore.hasEmptySignees && createStore.hasEmptyApprovers) {
    // Empty signees and approvers
    return i18n.t(
      'titles.undefined_approvers_and_signees_confirmation_request'
    );
  } else if (createStore.hasEmptyApprovers && createStore.hasEmptyObservers) {
    // Empty approvers and observers
    return i18n.t(
      'titles.undefined_approvers_and_observers_confirmation_request'
    );
  } else if (createStore.hasEmptyObservers && createStore.hasEmptySignees) {
    // Empty signees and observers
    return i18n.t(
      'titles.undefined_signees_and_observers_confirmation_request'
    );
  } else if (createStore.hasEmptySignees) {
    // Only empty signees
    return i18n.t('titles.undefined_signees_confirmation_request');
  } else if (createStore.hasEmptyApprovers) {
    // Only empty approvers
    return i18n.t('titles.undefined_approvers_confirmation_request');
  } else if (createStore.hasEmptyObservers) {
    // Only empty observers
    return i18n.t('titles.undefined_observers_confirmation_request');
  } else {
    return i18n.t('titles.no_observers_subscription_dialog_ask_owners');
  }
});

const buttonText = computed(() => {
  if (
    SignatureService.onlyMe(AuthService.user?.email, currentDocument.value) &&
    currentDocument.value?.signeesOrdered.flat().length === 1
  ) {
    return i18n.t('buttons.continue');
  }
  return i18n.t('buttons.send_invitations');
});

const signStatus = computed(() => {
  return signStore.document?.signContext.signStatus;
});

function isInvalidEmail(email) {
  return !EMAIL_REGEX.test(email);
}

function updateSignatureComment(newComment) {
  message.value = newComment;
}

function saveRecentlyUsedEmailsToPreferences() {
  if (isDocumentCreateAuthRoute.value) {
    try {
      saveRecentlyUsedEmails(currentDocument.value!.signeesOrdered.flat());
    } catch (e) {
      console.error('Save recently used emails failed ', e);
    }
  }
}

async function startDocumentProcess() {
  try {
    startDocumentPending.value = true;
    await deepSignAPI.value.documentsStart.update(
      currentDocument.value!.documentId
    );
    // The signing flow has started — drop its setup-wizard entry from preferences.
    removeDocumentWizard(currentDocument.value!.documentId);
    currentDocument.value.documentStatus = DocumentStatus.IN_PROGRESS;
    startDocumentPending.value = false;
    if (
      createStore.initiatorAsSignee &&
      createStore.filteredSignees.length === 1
    ) {
      await router.push({ path: destination.value, query: { ...route.query } });
    } else {
      emitter.$emit('open-invitations-dialog');
    }
  } catch (error) {
    startDocumentPending.value = false;
    console.error(error);
  }
}

function openCleanupConfirmationDialog() {
  confirmationDialog.value
    .open(confirmationTitle.value, confirmationText.value, null, {
      noCancel:
        !createStore.hasEmptyApprovers &&
        !createStore.hasEmptySignees &&
        !createStore.hasEmptyObservers,
      confirmActionText:
        !createStore.hasEmptyApprovers &&
        !createStore.hasEmptySignees &&
        !createStore.hasEmptyObservers
          ? i18n.t('buttons.close')
          : null
    })
    .then(async (result) => {
      if (result) {
        if (
          !createStore.hasEmptySignees &&
          !createStore.hasEmptyApprovers &&
          !createStore.hasEmptyObservers
        ) {
          return;
        }
        clearingEmptyInvitations.value = true;
        clearingEmptyObservers.value = true;
        await createStore.removeAllInviteeFieldsWithEmptyEmail();

        clearingEmptyInvitations.value = false;
        await createStore.removeAllObserversWithEmptyEmail();
        clearingEmptyObservers.value = false;
        await proceed();
      }
    });
}

async function validateInviteeForm() {
  const match = createStore.signeesOrderedFlat.find((item: Signee) =>
    isInvalidEmail(item.email ?? '')
  );

  if (match) {
    // The signee/approver list (and its email fields) only exists in the DOM
    // while the signing workflow dialog's Participants step is mounted. Open
    // it (or switch to that step if it's already open elsewhere) so
    // `focusInputField` has an actual element to focus instead of silently
    // no-op-ing against a detached list.
    if (!signingWorkflow.isOpen.value) {
      signingWorkflow.open(2);
      await nextTick();
    } else if (signingWorkflow.step.value !== 2) {
      signingWorkflow.goToStep(2);
      await nextTick();
    }
    await createStore.focusInputField(match.signeeId);
    return false;
  }

  return createStore.signeesOrderedFlat.every(
    (item: Signee) => item.email && !isInvalidEmail(item.email)
  );
}

async function proceed() {
  const isInviteeFormValid = await validateInviteeForm();
  if (!isInviteeFormValid) return;

  if (
    !createStore.hasEmptySignees &&
    !createStore.hasEmptyObservers &&
    !createStore.hasEmptyApprovers
  ) {
    saveRecentlyUsedEmailsToPreferences();
    await startDocumentProcess();
  } else {
    openCleanupConfirmationDialog();
  }
}

const hasOnlySignees = computed(() =>
  currentDocument.value?.signeesOrdered
    .flat()
    .every((signee) => signee.signeeType === SigneeType.SIGN)
);

const isEnvelope = computed(
  () => (currentDocument.value?.files?.length ?? 0) > 1
);

const userFields = computed(
  () =>
    currentDocument.value?.signeesOrdered
      .flat()
      .filter((signee) =>
        isGuest.value
          ? signee.email === userEmail.value
          : SignatureService.compareString(
              signee.email,
              AuthService.user?.email
            )
      ) || []
);

const shouldSign = computed(() => {
  if (isDocumentAuthRoute.value) return false;

  const hasPendingActions =
    userFields.value.length > 0
      ? userFields.value.every((signee) => {
          const emailMatch = isGuest.value
            ? signee.email === userEmail.value
            : SignatureService.compareString(
                signee.email,
                AuthService.user?.email
              );

          return (
            currentDocument.value &&
            emailMatch &&
            signee.signeeType === SigneeType.SIGN &&
            signee.signStatus === SignStatus.SIGNED &&
            (currentDocument.value?.documentStatus ===
              DocumentStatus.IN_PROGRESS ||
              currentDocument.value?.documentStatus === DocumentStatus.SIGNED ||
              currentDocument.value?.documentStatus ===
                DocumentStatus.WITHDRAWN)
          );
        })
      : false;

  return signStore.document?.documentPolicy?.canSign || hasPendingActions;
});

const shouldApprove = computed(() => {
  const hasPendingActions =
    userFields.value.length > 0
      ? userFields.value.every((signee) => {
          const emailMatch = isGuest.value
            ? signee.email === userEmail.value
            : SignatureService.compareString(
                signee.email,
                AuthService.user?.email
              );

          return (
            currentDocument.value &&
            emailMatch &&
            signee.signeeType === SigneeType.APPROVE &&
            signee.signStatus === SignStatus.APPROVED &&
            (currentDocument.value?.documentStatus ===
              DocumentStatus.IN_PROGRESS ||
              currentDocument.value?.documentStatus === DocumentStatus.SIGNED ||
              currentDocument.value?.documentStatus ===
                DocumentStatus.WITHDRAWN)
          );
        })
      : false;
  return signStore.document?.documentPolicy?.canApprove || hasPendingActions;
});

const isUserPartOfSignees = computed(() => {
  if (isGuest.value) {
    return SignatureService.shouldISign(
      userEmail.value,
      signStore.document?.signees || []
    );
  }
  return SignatureService.shouldISign(
    AuthService.user?.email,
    signStore.document?.signees || []
  );
});

const isSignedByUser = computed(() => {
  if (isGuest.value) {
    return SignatureService.isSignedByUser(
      userEmail.value,
      signStore.document?.signees || []
    );
  }
  return SignatureService.isSignedByUser(
    AuthService.user?.email,
    signStore.document?.signees || []
  );
});

function setAuthorityService(newAuthorityService) {
  authorityService.value = newAuthorityService;
}

function setUploadedFiles(newFiles) {
  uploadedFiles.value = newFiles;
}

function getAutographData() {
  if (autographData.value) {
    return base64ToFile(autographData.value, 'autograph.png');
  }
  if (
    signStore.document?.signContext.defaultAutographUrl &&
    typeof signStore.document?.signContext.defaultAutographUrl === 'string'
  ) {
    return base64ToFile(
      signStore.document.signContext.defaultAutographUrl,
      'autograph.png'
    );
  }
  return null;
}

function checkAsynchronousSign() {
  try {
    if (
      signStore.asynchronousSign?.signStatus === SignStatus.IN_PROGRESS &&
      signStore.asynchronousSign?.signKey &&
      signStore.document?.documentId
    ) {
      pollInterval.value = setInterval(() => {
        signStore.fetchAuthSignStatus(
          signStore.asynchronousSign.signKey,
          signStore.document.documentId
        );
      }, 2500);
      emitter.$emit('show-sign-pending-dialog', {
        document: currentDocument.value,
        authorityService: authorityService.value
      });
    }
  } catch (error) {
    signStore.signPending = false;
    console.error('Error polling sign status: ', error);
  }
}

function updateSignatureData(eventData) {
  autographData.value = eventData;
}

function updateAgreementStatus(eventData) {
  agreement.value = eventData;
}

function updateChangeAutograph() {
  useDefaultAutograph.value = false;
}

function stopPolling() {
  clearInterval(pollInterval.value);
}

async function buildSignPayload() {
  const signeeId = signStore.userSigneeId;
  const match = Array.from(currentPlaceholders.value as Placeholder[]).find(
    (placeholder: Placeholder) => {
      return (
        signeeId &&
        isPlaceholderForSignee(
          placeholder,
          signeeId,
          signStore.document?.signeeGroups
        ) &&
        placeholder.signeeType === SigneeType.SIGN
      );
    }
  );

  const signeePosition =
    signStore.document?.documentPolicy.canModifyAutographPosition && match
      ? await createPlaceholderPayload(match, placeholderTemplateRefs?.value)
      : null;

  return {
    sign: {
      comment: message.value,
      authorityService: !isGuest.value ? authorityService.value : null,
      autographPosition: signeePosition
    },
    autograph: await getAutographData()
  };
}

async function proceedWithSign(payload) {
  try {
    await signStore.signDocument(currentDocument.value.documentId, payload);
    signStore.signPending = false;
  } catch (error) {
    emitter.$emit('show-sign-error-dialog', {
      errorKey: error.response.data?.messageId,
      authorityService: authorityService.value,
      documentErrors: error.response.data?.details
    });
  }
}

async function sign() {
  try {
    signStore.signPending = true;
    let canProceed = false;

    // We need to show attachments hint if attachments are allowed and none was added
    if (
      signStore.document?.documentPolicy.canModifySigneeAttachments &&
      uploadedFiles.value.length === 0
    ) {
      await signConfirmationDialog.value
        .open(
          i18n.t('titles.no_attachments'),
          i18n.t('texts.no_attachments_hint'),
          null,
          { confirmActionText: i18n.t('buttons.sign_anyway') }
        )
        .then((result) => {
          if (result) {
            canProceed = true;
          } else {
            signStore.signPending = false;
            canProceed = false;
          }
        });
    } else {
      canProceed = true;
    }

    if (canProceed) {
      let payload = null;

      // When user is not authenticated he needs to confirm accepting agreements
      if (!AuthService.isAuthenticated) {
        await signConfirmationDialog.value
          .open(
            i18n.t('titles.signature_confirmation'),
            getTranslatedTerms('guest_ses'),
            null,
            { confirmActionText: i18n.t('buttons.confirm'), width: 417 }
          )
          .then((result) => {
            canProceed = !!result;
            signStore.signPending = canProceed;
          });
      } else if (
        currentDocument.value?.signatureMode === SignatureMode.ADVANCED &&
        authorityService.value === AuthorityService.MOBILE_ID
      ) {
        // SMS Code confirmation
        const textKey = getTranslatedTerms(currentDocument.value.jurisdiction);
        await signConfirmationDialog.value
          .open(i18n.t('titles.signature_confirmation'), textKey, null, {
            confirmActionText: i18n.t('buttons.confirm'),
            width: 417
          })
          .then((result) => {
            canProceed = result;
            signStore.signPending = canProceed;
          });
      }

      // Can finally sign the document
      if (canProceed) {
        payload = await buildSignPayload();

        // Sign SES as guest
        if (isGuest.value) {
          await proceedWithSign(payload);
        } else {
          // Sign as authenticated user
          if (!isBasic.value) {
            if (
              authorityService.value === AuthorityService.MOBILE_ID &&
              isMobileIdVerified.value
            ) {
              await preferencesStore.setPreference(
                'defaultAuthorityService',
                AuthorityService.MOBILE_ID
              );
            } else if (
              authorityService.value === AuthorityService.SIGN8 &&
              isSign8Verified.value
            ) {
              await preferencesStore.setPreference(
                'defaultAuthorityService',
                AuthorityService.SIGN8
              );
            } else {
              await preferencesStore.setPreference(
                'defaultAuthorityService',
                AuthorityService.DEEP_ID
              );
            }
            if (signStore.document?.signContext) {
              signStore.document.signContext.signStatus = null;
              signStore.mobileIdCheckPending = true;
            }
            await proceedWithSign(payload);
            checkAsynchronousSign();
            signStore.mobileIdCheckPending = false;
          } else {
            await proceedWithSign(payload);
          }
        }
      }
    }
  } catch (error) {
    signStore.signPending = false;
    console.error('Error signing the document', error);
  }
}

function isValidHexColor(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value.trim());
}

// Only trigger after the API calls were done.
watch(
  () => isLoadingOrganizationData.value,
  (newValue) => {
    if (
      brandingsQuery.data.value?.primaryColor &&
      isValidHexColor(brandingsQuery.data.value?.primaryColor) &&
      !newValue
    ) {
      theme.themes.value.light.colors.primary =
        brandingsQuery.data.value?.primaryColor;
      theme.themes.value.dark.colors.primary =
        brandingsQuery.data.value?.primaryColor;
    } else {
      return;
    }
  },
  { immediate: true }
);

watch(
  () => signStatus.value,
  async (newValue, oldValue) => {
    if (newValue === SignStatus.IN_PROGRESS && oldValue !== newValue) {
      await signStore.fetchAuthSignStatus(
        signStore.asynchronousSign.signKey,
        signStore.document.documentId
      );
    }
    if (newValue !== SignStatus.IN_PROGRESS) {
      stopPolling();
      emitter.$emit('close-sign-pending-dialog');
      // If signStatus is 'pending', we have to check if there is an error and display it
      if (newValue === SignStatus.PENDING) {
        if (!signStore.document?.signContext.lastError) {
          return;
        } else if (
          signStore.document.signContext.lastError?.messageId ===
          'error.sign.user.timeout'
        ) {
          emitter.$emit('show-timeout-dialog');
        } else if (
          signStore.document.signContext.lastError?.messageId ===
          'error.sign.user.cancel'
        ) {
          emitter.$emit('show-signature-cancelled-dialog', {
            authorityService: authorityService.value
          });
        } else {
          if (
            signStore.document?.signContext.lastError?.messageId ===
              'error.sign.user.mobileId.not-activated' &&
            authorityService.value === AuthorityService.MOBILE_ID
          ) {
            emitter.$emit('show-sign-pending-dialog', {
              document: currentDocument.value,
              authorityService: authorityService.value
            });
            return;
          }
          emitter.$emit('show-sign-error-dialog', {
            errorKey: signStore.document.signContext.lastError?.messageId,
            authorityService: authorityService.value
          });
        }
      }

      // If signStatus is 'signed' check if we need to redirect to the correct page
      if (newValue === SignStatus.SIGNED) {
        stopPolling();

        // Need to do this check to avoid showing the success message twice
        if (newValue !== oldValue) {
          if (!isGuest.value && isSignedByUser.value && currentDocument.value) {
            await router.push({
              path: `/document/${currentDocument.value.documentId}/details`,
              query: { ...route.query }
            });
          }

          emitter.$emit('update-sign-status');

          const isEnvelope = (signStore.document?.files?.length ?? 0) > 1;
          messageStore.showMessage({
            text: isSignatureTypeSeal.value
              ? i18n.t('snackbars.sealed_text')
              : isEnvelope
                ? i18n.t('snackbars.signed_text_envelope', {
                    n: signStore.document?.files?.length
                  })
                : i18n.t('snackbars.signed_text'),
            color: 'success',
            icon: 'fa fa-circle-check'
          });
        }
      }
    }
  },
  {
    deep: true
  }
);

// needed in order to sync or reset the autograph
watch(
  () => signStore.autographData,
  (newValue) => {
    autographData.value = newValue;
  }
);
</script>
<style scoped lang="scss">
@use 'sass:map';
@use '@/styles/settings.scss' as *;

.navigation-drawer {
  @media #{map.get($display-breakpoints, 'md-and-down')} {
    max-height: 93dvh;
  }
}

.toolbar {
  @media #{map.get($display-breakpoints, 'lg-and-up')} {
    border-bottom-width: 1px;
    padding: 0 !important;
  }
}

.drop-shadow {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2) !important;
}

.custom-brand-image {
  height: 24px;

  @media #{map.get($display-breakpoints, 'lg-and-up')} {
    height: 32px;
  }
}

.deep-app-logo {
  height: 16px;
  filter: grayscale(100%);
}
</style>
